import { useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';

/**
 * End a signed-in session after an hour with no interaction.
 *
 * Firebase Auth persists to localStorage by default, which is the right trade for a phone
 * companion but means a browser left open on the list keeps showing it indefinitely —
 * through a closed tab, a closed browser and a reboot. A note holds whatever its author
 * typed into it, so a screen left unattended on a shared machine is the exposure this
 * closes.
 *
 * Idle, not absolute: the clock measures time since the last interaction, so an hour of
 * actual use never interrupts anyone, and an hour of nothing ends the session.
 */
export const IDLE_LIMIT_MS = 60 * 60 * 1000;

/** Last interaction. In localStorage so every tab shares one clock and a reload keeps it. */
const LAST_ACTIVITY_KEY = 'zsoogi:lastActivity';

/** Set when this hook ends a session, so SignIn can say why instead of looking broken. */
const IDLE_NOTICE_KEY = 'zsoogi:idleSignOut';

/** Recording every scroll tick would write the same minute to storage hundreds of times. */
const WRITE_THROTTLE_MS = 30_000;

/** Timers in a background tab are throttled, so compare timestamps on a poll as well. */
const POLL_MS = 30_000;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'scroll', 'touchstart'] as const;

/**
 * Fallback for when storage is unavailable — private browsing, or blocked site data, where
 * the accessors throw rather than return null. The timeout then works within the page but
 * not across a reload, which is better than failing to sign in at all.
 */
let memoryLastActivity: number | null = null;

function readStoredActivity(): number | null {
  try {
    const raw = window.localStorage.getItem(LAST_ACTIVITY_KEY);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function lastActivity(): number | null {
  return readStoredActivity() ?? memoryLastActivity;
}

function touch(at: number): void {
  memoryLastActivity = at;
  try {
    window.localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    // Storage blocked or full. The in-memory copy still bounds this page's session.
  }
}

function forgetActivity(): void {
  memoryLastActivity = null;
  try {
    window.localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    // As above — nothing to do, and nothing worth failing a sign-out over.
  }
}

/** Whether the last session ended on the idle timeout. Does not clear the flag. */
export function peekIdleSignOutNotice(): boolean {
  try {
    return window.sessionStorage.getItem(IDLE_NOTICE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Clear the notice once it has been shown. Idempotent, so StrictMode may call it twice. */
export function clearIdleSignOutNotice(): void {
  try {
    window.sessionStorage.removeItem(IDLE_NOTICE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/**
 * Watch for inactivity and sign out when it passes an hour.
 *
 * `ready` is the auth state having resolved — not merely `user` being set. Before that,
 * `user` is null because Firebase is still restoring the session from storage, and treating
 * that as signed-out would clear the very timestamp needed to expire a restored session.
 */
export function useIdleSignOut(uid: string | undefined, ready: boolean): void {
  useEffect(() => {
    if (!ready) return;

    if (!uid) {
      // Signed out, by hand or by this hook. Clearing matters: a timestamp left behind
      // would expire the next sign-in the instant it happened.
      forgetActivity();
      return;
    }

    const expire = () => {
      try {
        window.sessionStorage.setItem(IDLE_NOTICE_KEY, '1');
      } catch {
        // The sign-out matters; explaining it afterwards is a nicety.
      }
      forgetActivity();
      void signOut(auth);
    };

    const check = () => {
      const last = lastActivity();
      if (last === null) {
        touch(Date.now());
        return;
      }
      if (Date.now() - last >= IDLE_LIMIT_MS) expire();
    };

    // A session restored from storage is measured from when it was last used, not from now.
    // Otherwise closing the tab overnight and reopening it would reset the clock, which is
    // the case this is mostly here for.
    check();

    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite < WRITE_THROTTLE_MS) return;
      lastWrite = now;
      touch(now);
    };

    const onReturn = () => {
      if (document.visibilityState === 'visible') check();
    };

    const poll = window.setInterval(check, POLL_MS);
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    for (const type of ACTIVITY_EVENTS) {
      window.addEventListener(type, onActivity, { passive: true });
    }

    return () => {
      window.clearInterval(poll);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
      for (const type of ACTIVITY_EVENTS) {
        window.removeEventListener(type, onActivity);
      }
    };
  }, [uid, ready]);
}
